import { action, observable } from "mobx";
import ChargeRegistrationRepository from "repositories/chargeRegistrationRepository";
import EshopError from "../models/eshopError";
import ChargeTypesService from "./chargeTypesService";
import EventBus, {
  chargeRegistration,
  chargeRegistrationReport,
  vehicleChargingInstructions,
  vehicleRegistration,
  vehicleValidityInstruction,
} from "./eventBus";
import type { IVehicle } from "../models/types";
import ChargeValidityInstructionsResponse from "entities/chargeValidityInstructionsResponse";
import VehicleRepository from "../repositories/vehicleRepository";
import ChargeRegistrationsResponse from "entities/chargeRegistrationsResponse";
import VehicleChargingInstructionsResponse from "entities/vehicleChargingInstructionsResponse";

export enum RequestSource {
  NewValidity = "NEW_VALIDITY",
  NewLicensePlate = "NEW_LICENSE_PLATE",
}

export default class ChargeRegistrationService {
  @observable chargeRegistrationsResponse: ChargeRegistrationsResponse;
  @observable vehicleChargingInstructions: VehicleChargingInstructionsResponse | undefined;
  @observable vehicle?: IVehicle;

  constructor(
    private chargeRegistrationRepository: ChargeRegistrationRepository,
    private vehicleRepository: VehicleRepository,
    private eventBus: EventBus,
    private chargeTypesService: ChargeTypesService
  ) {}

  @action.bound
  getRegistrationReport(countryId: string, licensePlates: string[], validSince?: Date) {
    return this.chargeRegistrationRepository
      .vehicleRegistrationsReport(countryId, licensePlates, validSince)
      .then(response => {
        this.eventBus.publish(chargeRegistrationReport({ report: response instanceof EshopError ? undefined : response }));
      })
      .catch(() => this.eventBus.publish(chargeRegistrationReport({ report: undefined })));
  }

  @action.bound
  async getVehicleRegistration(countryId: string, licensePlate: string, validSince?: Date, source?: RequestSource) {
    const response = await this.chargeRegistrationRepository.vehicleRegistration(countryId, licensePlate, validSince);

    if (response instanceof EshopError) {
      throw response;
    }
    this.eventBus.publish(vehicleRegistration({ vehicleRegistration: response, source }));
  }

  @action.bound
  async getChargingInstructions(countryId: string, licensePlate: string) {
    return await this.vehicleRepository
      .getChargingInstruction(countryId, licensePlate)
      .then(response => {
        this.vehicleChargingInstructions =
          response instanceof EshopError ? undefined : (response as VehicleChargingInstructionsResponse);
      })
      .catch(() => this.eventBus.publish(vehicleChargingInstructions({ vehicleChargingInstructions: undefined })));
  }

  @action.bound
  getChargeRegistration(countryId: string, licensePlate: string, source?: RequestSource) {
    return this.chargeRegistrationRepository.getWithCharge(countryId, licensePlate).then(response => {
      if (response instanceof EshopError) {
        // Throw error in case of unexpected server errors (non 404)
        throw response;
      } else {
        this.chargeRegistrationsResponse = response;
        this.vehicle = {
          countryId,
          licensePlate,
        };

        this.eventBus.publish(chargeRegistration({ chargeRegistrationsResponse: response, source }));
      }
    });
  }

  @action.bound
  async getProposedValidity(
    countryId: string,
    licensePlate: string,
    priceListItemId: string,
    hasImmediateValidity?: boolean,
    validSinceDate?: Date,
    source?: RequestSource
  ) {
    const response = await this.vehicleRepository.proposedValidity(
      countryId,
      licensePlate,
      priceListItemId,
      hasImmediateValidity,
      validSinceDate
    );

    if (response instanceof ChargeValidityInstructionsResponse) {
      this.eventBus.publish(vehicleValidityInstruction({ validityInstruction: response, source }));
    } else {
      throw response;
    }
  }

  @action.bound
  reset() {
    this.chargeRegistrationsResponse = new ChargeRegistrationsResponse();
    this.vehicle = undefined;
  }
}
